"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { Moon, Sun, SunMoon, X } from "lucide-react";
import { useTheme } from "next-themes";

import { cn } from "@/lib/utils";

const SPRING = { type: "spring" as const, stiffness: 260, damping: 18 };

// Hydration gate as a static external store — server snapshot renders
// nothing, client snapshot flips to the real UI in a single pre-paint
// commit (no post-mount setState, no extra render).
const emptySubscribe = () => {
  return () => {
    // Nothing to unsubscribe — the snapshot never changes post-hydration.
  };
};
const getHydratedSnapshot = () => true;
const getHydratedServerSnapshot = () => false;

export function FloatingThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const hydrated = useSyncExternalStore(
    emptySubscribe,
    getHydratedSnapshot,
    getHydratedServerSnapshot,
  );
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on Escape and click-outside while open
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  if (!hydrated) return null;
  // The docs section has its own theme toggle in the header, so the
  // floating one would be a duplicate there.
  if (pathname?.startsWith("/docs")) return null;

  const isDark = resolvedTheme === "dark";

  const pick = (theme: "light" | "dark") => {
    setTheme(theme);
    setOpen(false);
  };

  const optionBase =
    "flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-200 cursor-pointer";
  const activeOption =
    "bg-rl-accent border-rl-accent text-[#08090c] shadow-lg";
  const inactiveOption =
    "bg-rl-surface2 border-rl-border2 text-rl-text2 hover:border-rl-accent hover:text-foreground";

  return (
    <LazyMotion features={domAnimation}>
      <div
        ref={containerRef}
        className="fixed right-6 bottom-6 z-50 flex flex-col items-center gap-3"
      >
      <AnimatePresence>
        {open && (
          <>
            <m.button
              key="light"
              type="button"
              onClick={() => pick("light")}
              aria-label="Switch to light theme"
              aria-pressed={!isDark}
              initial={{ opacity: 0, y: 10, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.8 }}
              transition={{ ...SPRING, delay: 0.06 }}
              className={cn(
                optionBase,
                !isDark ? activeOption : inactiveOption,
              )}
            >
              <Sun size={18} />
            </m.button>
            <m.button
              key="dark"
              type="button"
              onClick={() => pick("dark")}
              aria-label="Switch to dark theme"
              aria-pressed={isDark}
              initial={{ opacity: 0, y: 10, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.8 }}
              transition={SPRING}
              className={cn(
                optionBase,
                isDark ? activeOption : inactiveOption,
              )}
            >
              <Moon size={18} />
            </m.button>
          </>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Change theme"
        aria-expanded={open}
        className="bg-rl-surface2 border-rl-border2 hover:border-rl-accent text-foreground flex size-12 cursor-pointer items-center justify-center rounded-full border shadow-lg transition-colors duration-200"
      >
        {open ? <X size={20} /> : <SunMoon size={20} />}
      </button>
      </div>
    </LazyMotion>
  );
}
